/**
 * Canonical time display for Coachify (US standard: 12-hour with AM/PM).
 *
 * Use for all user-facing times:
 * - `formatUsTime(utcInstant, timeZone)` — single instant
 * - `formatUsTimeRange(startUtc, endUtc, timeZone)` — range e.g. 3:00 PM – 3:15 PM
 * - `utcTimeStringToDisplay(utcHHmm, timeZone)` — UTC-stored HH:mm strings (scheduling blocks)
 *
 * For timezone resolution use `resolveViewerTimezone` / `resolveCoachTimezone` /
 * `resolveCustomerTimezone` / `formatForViewer` / `formatInboxMessageTimestamp`.
 * Do not use `toLocaleTimeString` or military (24-hour) format in UI.
 */
import moment from 'moment-timezone';
import { getDefaultTimeZoneOnBrowser } from './dates';

export const DEFAULT_TZ = 'Etc/UTC';

/** Canonical US display: 12-hour clock with AM/PM (e.g. 3:00 PM). Use for all user-facing times. */
export const US_TIME_FORMAT_OPTIONS = Object.freeze({
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

export const normalizeTz = tz => {
  const s = typeof tz === 'string' ? tz.trim() : '';
  return s || DEFAULT_TZ;
};

/** Stored when timezone was unknown at booking — not a real viewer zone. */
export const isUtcPlaceholderTimezone = tz => {
  const n = normalizeTz(tz);
  return n === 'Etc/UTC' || n === 'UTC' || n === 'GMT';
};

export const toUtcDate = value => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const getBrowserTimezone = () => {
  if (typeof window === 'undefined') return DEFAULT_TZ;
  try {
    return normalizeTz(getDefaultTimeZoneOnBrowser());
  } catch {
    return DEFAULT_TZ;
  }
};

/**
 * Coach profile timezone for viewing own calendar, inbox, and bookings.
 * booking TZ (session was scheduled) → profile → browser → UTC.
 */
export const resolveCoachViewerTimezone = ({
  user,
  browserTz = getBrowserTimezone(),
  bookingTimezone,
} = {}) => {
  const fromBooking =
    bookingTimezone && !isUtcPlaceholderTimezone(bookingTimezone) ? bookingTimezone : null;
  const profileTz = user?.attributes?.profile?.publicData?.timezone;
  const fromProfile = profileTz && !isUtcPlaceholderTimezone(profileTz) ? profileTz : null;
  return normalizeTz(fromBooking || fromProfile || browserTz);
};

/**
 * Coach/listing timezone for availability and slot conversion (customer booking UI).
 * profile → listing availabilityPlan → browser → UTC.
 */
export const resolveCoachTimezone = ({
  user,
  listing,
  listingTimezone,
  coachTimezone,
  browserTz = getBrowserTimezone(),
} = {}) => {
  const fromExplicit =
    coachTimezone && !isUtcPlaceholderTimezone(coachTimezone) ? coachTimezone : null;
  const profileTz = user?.attributes?.profile?.publicData?.timezone;
  const fromUser = profileTz && !isUtcPlaceholderTimezone(profileTz) ? profileTz : null;
  const listingTzRaw = listingTimezone || listing?.attributes?.availabilityPlan?.timezone;
  const fromListing =
    listingTzRaw && !isUtcPlaceholderTimezone(listingTzRaw) ? listingTzRaw : null;
  return normalizeTz(fromExplicit || fromUser || fromListing || browserTz);
};

/**
 * Customer timezone for display and booking UI: profile → browser → UTC.
 * Optional `customerTimezone` is a fallback when profile is unset (e.g. legacy booking row).
 * Ignores Etc/UTC placeholders saved when timezone was missing at checkout.
 */
export const resolveCustomerTimezone = ({
  user,
  customerTimezone,
  browserTz = getBrowserTimezone(),
} = {}) => {
  const profileTz = user?.attributes?.profile?.publicData?.timezone;
  const fromProfile = profileTz && !isUtcPlaceholderTimezone(profileTz) ? profileTz : null;
  const fromBooking =
    customerTimezone && !isUtcPlaceholderTimezone(customerTimezone) ? customerTimezone : null;
  return normalizeTz(fromProfile || fromBooking || browserTz);
};

/**
 * Timezone for the logged-in viewer (inbox, calendars, booking messages).
 * Coach: profile → browser. Customer: profile → browser.
 */
export const resolveViewerTimezone = ({
  role = 'customer',
  user,
  browserTz = getBrowserTimezone(),
} = {}) => {
  if (role === 'coach' || role === 'provider') {
    return resolveCoachViewerTimezone({ user, browserTz });
  }
  return resolveCustomerTimezone({ user, browserTz });
};

/** YYYY-MM-DD calendar key for a UTC instant in the given IANA timezone. */
export const toDateKeyInTimezone = (utcInstant, timeZone) => {
  const d = toUtcDate(utcInstant);
  if (!d) return '';
  return moment(d).tz(normalizeTz(timeZone)).format('YYYY-MM-DD');
};

/**
 * Inclusive UTC instants for the first and last moment of a calendar month in a timezone.
 * monthIndex is 0-based (0 = January), matching JavaScript Date#getMonth().
 */
export const getCalendarMonthUtcRange = (year, monthIndex, timeZone) => {
  const tz = normalizeTz(timeZone);
  const monthStart = moment.tz({ year, month: monthIndex, day: 1 }, tz).startOf('day');
  const monthEnd = monthStart.clone().endOf('month');
  return {
    start: monthStart.toDate(),
    end: monthEnd.toDate(),
    startDateKey: monthStart.format('YYYY-MM-DD'),
    endDateKey: monthEnd.format('YYYY-MM-DD'),
  };
};

/** Whether a booking UTC start falls on a day within the calendar month (viewer timezone). */
export const bookingStartInCalendarMonth = (utcInstant, year, monthIndex, timeZone) => {
  const dateKey = toDateKeyInTimezone(utcInstant, timeZone);
  if (!dateKey) return false;
  const { startDateKey, endDateKey } = getCalendarMonthUtcRange(year, monthIndex, timeZone);
  return dateKey >= startDateKey && dateKey <= endDateKey;
};

/**
 * Customer calendar: show accepted/confirmed/completed/cancelled bookings.
 * Hide coach-not-yet-accepted requests (pending / request / preauthorized).
 */
export const isCustomerCalendarBookingVisible = row => {
  if (!row) return false;
  const lastTransition = String(row.attributes?.lastTransition || '');
  const state = String(row.attributes?.state || '').toLowerCase();

  if (
    [
      'transition/cancel',
      'transition/cancel-no-refund',
      'transition/cancel-from-disputed',
    ].includes(lastTransition) ||
    state.includes('cancelled') ||
    state.includes('canceled')
  ) {
    return true;
  }

  if (
    state.includes('delivered') ||
    state.includes('complete') ||
    state.includes('accepted') ||
    lastTransition.includes('accept') ||
    lastTransition.includes('complete')
  ) {
    return true;
  }

  if (
    state.includes('pending') ||
    lastTransition.includes('request') ||
    lastTransition === 'transition/confirm-payment'
  ) {
    return false;
  }

  return true;
};

/**
 * Format message/thread timestamps in the viewer's profile timezone.
 */
export const formatInboxMessageTimestamp = (value, { user, isCoach = false, locale = 'en-US' } = {}) => {
  const d = toUtcDate(value);
  if (!d) return '';
  const tz = resolveViewerTimezone({
    role: isCoach ? 'coach' : 'customer',
    user,
  });
  return d.toLocaleString(locale, {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...US_TIME_FORMAT_OPTIONS,
  });
};

export const formatBookingDate = (utcInstant, timeZone, locale = 'en-US', options = {}) => {
  const d = toUtcDate(utcInstant);
  if (!d) return '';
  return d.toLocaleDateString(locale, {
    timeZone: normalizeTz(timeZone),
    weekday: options.weekday,
    month: options.month || 'short',
    day: options.day || 'numeric',
    year: options.year,
    ...options,
  });
};

/**
 * Format a UTC instant for display in US standard 12-hour time (e.g. 3:00 PM).
 * This is the single canonical helper for user-facing clock times in the app.
 */
export const formatUsTime = (utcInstant, timeZone, locale = 'en-US', options = {}) => {
  const d = toUtcDate(utcInstant);
  if (!d) return '';
  return d.toLocaleTimeString(locale, {
    ...US_TIME_FORMAT_OPTIONS,
    timeZone: normalizeTz(timeZone),
    ...options,
    hour12: true,
  });
};

/** @alias formatUsTime */
export const formatBookingTime = formatUsTime;

/** US 12-hour range, e.g. 3:00 PM – 3:15 PM */
export const formatUsTimeRange = (startUtc, endUtc, timeZone, locale = 'en-US', timeOptions = {}) => {
  const start = formatUsTime(startUtc, timeZone, locale, timeOptions);
  if (!start) return '';
  const end = formatUsTime(endUtc, timeZone, locale, timeOptions);
  return end ? `${start} – ${end}` : start;
};

/** @alias formatUsTimeRange */
export const formatBookingTimeRange = formatUsTimeRange;

export const formatUsDateTime = (utcInstant, timeZone, locale = 'en-US') => {
  const d = toUtcDate(utcInstant);
  if (!d) return '';
  const tz = normalizeTz(timeZone);
  const dateStr = formatBookingDate(d, tz, locale, { month: 'long', day: 'numeric', year: 'numeric' });
  const timeStr = formatUsTime(d, tz, locale);
  return timeStr ? `${dateStr} at ${timeStr}` : dateStr;
};

/** @alias formatUsDateTime */
export const formatBookingDateTime = formatUsDateTime;

/** UTC-stored HH:mm wall clock → US 12-hour display in target IANA zone (e.g. 3:00 PM). */
export const utcTimeStringToDisplay = (utcHHmm, timeZone) => {
  if (!utcHHmm) return utcHHmm;
  const todayStr = moment().format('YYYY-MM-DD');
  return moment
    .utc(`${todayStr} ${utcHHmm}`, 'YYYY-MM-DD HH:mm')
    .tz(normalizeTz(timeZone))
    .format('h:mm A');
};

/** UTC HH:mm → local 24h HH:mm (form inputs / storage only — not for UI display). */
export const utcTimeStringToDisplay24Hour = (utcHHmm, timeZone) => {
  if (!utcHHmm) return utcHHmm;
  const todayStr = moment().format('YYYY-MM-DD');
  return moment
    .utc(`${todayStr} ${utcHHmm}`, 'YYYY-MM-DD HH:mm')
    .tz(normalizeTz(timeZone))
    .format('HH:mm');
};

/** Local HH:mm in target zone → UTC HH:mm for storage. */
export const displayTimeToUtcTimeString = (localHHmm, timeZone) => {
  if (!localHHmm) return localHHmm;
  const todayStr = moment().format('YYYY-MM-DD');
  return moment.tz(`${todayStr} ${localHHmm}`, 'YYYY-MM-DD HH:mm', normalizeTz(timeZone)).utc().format('HH:mm');
};

export const formatForViewer = ({
  startUtc,
  endUtc,
  role = 'customer',
  user,
  listing,
  coachTimezone,
  locale = 'en-US',
  browserTz = getBrowserTimezone(),
} = {}) => {
  const tz =
    role === 'coach' || role === 'provider'
      ? resolveCoachViewerTimezone({ user, browserTz, bookingTimezone: coachTimezone })
      : resolveViewerTimezone({
          role,
          user,
          listing,
          browserTz,
        });
  const time12 = formatUsTime(startUtc, tz, locale);
  return {
    timeZone: tz,
    date: formatBookingDate(startUtc, tz, locale, { weekday: 'short', month: 'short', day: 'numeric' }),
    time: time12,
    time12,
    range: formatUsTimeRange(startUtc, endUtc, tz, locale),
    dateTime: formatUsDateTime(startUtc, tz, locale),
  };
};

/** Parse booking-group occurrence id: dateKey|startIso|endIso */
export const parseBookingOccurrenceId = occurrenceId => {
  const raw = typeof occurrenceId === 'string' ? occurrenceId.trim() : '';
  if (!raw || !raw.includes('|')) return null;
  const parts = raw.split('|');
  if (parts.length < 3) return null;
  const [dateKey, startIso, endIso] = parts;
  const startTime = toUtcDate(startIso);
  const endTime = toUtcDate(endIso);
  if (!startTime || !endTime) return null;
  return { dateKey, startTime, endTime };
};

/**
 * Reformat booking-group sourceLabel (stored with server-local/UTC slot text) for the viewer.
 */
export const formatBookingGroupSourceLabel = ({
  sourceLabel,
  sourceOccurrenceId,
  sourceName,
  slotStartTime,
  slotEndTime,
  role = 'customer',
  user,
  listing,
} = {}) => {
  const parsed = parseBookingOccurrenceId(sourceOccurrenceId);
  const startUtc = parsed?.startTime || toUtcDate(slotStartTime);
  const endUtc = parsed?.endTime || toUtcDate(slotEndTime);
  if (!startUtc) return sourceLabel || '';

  const labels = formatForViewer({
    startUtc,
    endUtc,
    role,
    user,
    listing,
  });
  const datePart = formatBookingDate(startUtc, labels.timeZone, 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timePart = labels.range;

  if (sourceLabel && String(sourceLabel).includes('—')) {
    const parts = String(sourceLabel)
      .split('—')
      .map(p => p.trim());
    if (parts.length >= 3) {
      return `${parts[0]} — ${datePart} — ${timePart}`;
    }
    if (parts.length === 2) {
      return `${parts[0]} — ${timePart}`;
    }
  }

  const name = sourceName || '';
  return name ? `${name} — ${datePart} — ${timePart}` : `${datePart} — ${timePart}`;
};

/**
 * Role-aware display text for booking thread messages (ignores stale server-stored body).
 */
export const formatBookingMessageDisplayText = (
  message,
  { isCoach = false, bookingStart, bookingEnd, sessionName, user } = {}
) => {
  const md = message?.metadata || {};
  const kind = md.kind;
  const startUtc = md.startTime || bookingStart;
  if (!startUtc) return message?.body || '';

  const role = isCoach ? 'coach' : 'customer';
  const labels = formatForViewer({
    startUtc,
    endUtc: md.endTime || bookingEnd,
    role,
    user,
    coachTimezone: md.timezone,
  });
  const name = md.sourceName || md.sessionName || sessionName || 'Session';

  if (kind === 'booking_request' || String(message?.body || '').startsWith('New booking request for')) {
    return `New booking request for ${name} on ${labels.dateTime}.`;
  }
  if (kind === 'booking_accepted' || String(message?.body || '').startsWith('Your booking has been approved')) {
    return `Your booking has been approved. You are scheduled for ${name} on ${labels.dateTime}.`;
  }
  return message?.body || '';
};

/**
 * Reformat thread list preview when booking metadata or bookingStart is available.
 */
export const formatBookingThreadPreview = ({
  text,
  isCoach = false,
  bookingStart,
  bookingEnd,
  sessionName,
  metadata,
  user,
  listing,
} = {}) => {
  if (metadata?.startTime || metadata?.kind) {
    return formatBookingMessageDisplayText({ body: text, metadata }, { isCoach, user });
  }

  const startUtc = bookingStart;
  if (!startUtc || !text) return text || '';

  const role = isCoach ? 'coach' : 'customer';
  const labels = formatForViewer({
    startUtc,
    endUtc: bookingEnd,
    role,
    user,
    listing,
  });
  const name = sessionName || 'Session';

  if (String(text).startsWith('New booking request for')) {
    return `New booking request for ${name} on ${labels.dateTime}.`;
  }
  if (String(text).startsWith('Your booking has been approved')) {
    return `Your booking has been approved. You are scheduled for ${name} on ${labels.dateTime}.`;
  }
  return text;
};
